import {useCallback, useMemo, useState} from 'react'
import {ScrollView, StyleSheet, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {DOCUMENT_PAGE_MAX_QUESTIONS} from '#/lib/document/limits'
import {
  type DocumentPageMaterial,
  MATERIAL_QUESTION_TYPES,
  type MaterialQuestionType,
  normalizeQuestion,
  pageMaterialDraftKey,
} from '#/lib/document/material'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {createSlide} from '#/lib/slideFactory'
import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import * as Toast from '#/view/com/util/Toast'
import {MultiChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/MultiChoiceSlideEditor'
import {SingleChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/SingleChoiceSlideEditor'
import {TrueFalseSlideEditor} from '#/screens/CapsuleStudio/components/slides/TrueFalseSlideEditor'
import * as Dialog from '#/components/Dialog'
import {FlashBookAudioField} from '#/components/flashbooks/FlashBookAudioField'
import {StudioConfirmDialog} from '#/components/studio/StudioConfirmDialog'
import {Text} from '#/components/Typography'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type Slide, SLIDE_TYPE_META} from '#/types/capsule'

/**
 * Authoring the audio and the questions a PDF page carries.
 *
 * The card opens *over the page it is about* — bottom-anchored inside the
 * carousel, the way the alt-text bar is — rather than as a full-screen sheet.
 * That is not only a matter of taste: the author is judging a page, and a dialog
 * that covers the page they are writing about takes away the thing they are
 * looking at. The card can be dismissed, and it never covers the page's top third.
 *
 * The two sections are deliberately *not* re-implementations. The clip is the
 * capsule studio's own audio field, with upload, recording, editing and clearing
 * exactly as they behave there; the questions are the capsule studio's own
 * editors, mounted inline, so a single choice question written on a PDF page is
 * the same object, written by the same code, as one written in a capsule. This
 * card contributes the page: the header that says which one, the counter that
 * cannot exceed {@link DOCUMENT_PAGE_MAX_QUESTIONS}, and a Confirm that is the
 * only thing that touches the post.
 *
 * Nothing is committed until Confirm. That is what makes Cancel mean something,
 * and what lets the author open the card, change their mind about a question and
 * close it without the document having moved on in the meantime.
 *
 * Because Confirm is the only thing that publishes, every destructive press in
 * here is *recoverable* — Cancel puts back what the page holds today. It is not
 * recoverable in the only place that matters, though: the words of a question the
 * author has just written exist nowhere else, and clearing the questions or
 * deleting one throws them away on the spot. So both ask first, in the app's own
 * dialog, the way the studios ask before deleting a block.
 *
 * The card's own controls are not the only way out of it. A reader that is also
 * an author can turn the page, or leave the reader, from controls that belong to
 * the surface above — and any of those unmounts this card and takes the draft with
 * it. That is what `onDirtyChange` is for: the card says what it is holding, and
 * the surface stops at the door and asks. It says it *at the change* rather than
 * after the render that change causes (see `announce`), because the hand that
 * types a prompt and reaches for the next page in the same turn is not waiting
 * for a commit.
 */
export function PageMaterialEditor({
  page,
  initial,
  accent,
  onConfirm,
  onCancel,
  onDirtyChange,
}: {
  /** 1-based page the card is open on. */
  page: number
  /** What the page holds today, if anything. */
  initial?: DocumentPageMaterial
  /** The document's own accent, so the card belongs to it. */
  accent: string
  /** Commit — `undefined` when the page has been emptied. */
  onConfirm: (material: DocumentPageMaterial | undefined) => void
  onCancel: () => void
  /**
   * Whether there is anything in the card that the page does not have yet.
   *
   * Reported upwards because the card is not the only thing that can close it: a
   * reader that is also an author can turn the page, or leave the reader
   * entirely, from controls that are not this card's — and every one of those
   * takes the draft with it. The card cannot refuse them (it does not own them);
   * it can only say that there is something to lose, and let the surface ask.
   *
   * Told as the field changes rather than after the card has rendered for it, so
   * that a door taken in the same turn as the keystroke still finds it.
   */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const ss = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()

  const [audioUrl, setAudioUrl] = useState<string | undefined>(
    initial?.audioUrl,
  )
  const [questions, setQuestions] = useState<Slide[]>(
    () => initial?.questions ?? [],
  )

  /**
   * The page's own material, and the card's report of what it holds instead.
   *
   * The report is said **at the change**, from the handler that made it, rather
   * than from an effect watching a derived draft. An effect is a commit later: a
   * reader who types a prompt and takes a door in the same turn — a fast hand, or
   * a driven run — asks the guard before
   * the report has been committed, and the page turns with the question on it.
   * The surface keeps this in state of its own, so a report made during the
   * keystroke rides the same React commit as the keystroke.
   *
   * It is a comparison rather than a "something happened" flag, so the report is
   * also the way back: words typed and taken out again leave the card holding what
   * the page already has, and a row holding nothing but a type and an id is not a
   * draft either (`pageMaterialDraftKey` skips those).
   *
   * `pageKey` is the baseline every report is measured against; `announce` is what
   * every change reports through, and the only two fields go through it too.
   */
  const pageKey = useMemo(
    () =>
      pageMaterialDraftKey({
        audioUrl: initial?.audioUrl,
        questions: initial?.questions ?? [],
      }),
    [initial],
  )

  /*
   * The report, through a ref.
   *
   * `announce` is called by every change, and its identity is read by the fields it
   * is handed to — so a caller that re-renders (a live page under a composer) would
   * otherwise re-make the card's own commit path on every one of its renders. The
   * report is still made *at* the change: the wrapper runs whatever `onDirtyChange`
   * the surface holds at that moment, which is the same one an effect would have
   * reached one commit later (`useNonReactiveCallback`).
   */
  const reportDirty = useNonReactiveCallback(onDirtyChange)

  const announce = useCallback(
    (draft: {audioUrl?: string; questions: readonly Slide[]}) => {
      reportDirty(pageMaterialDraftKey(draft) !== pageKey)
    },
    [pageKey, reportDirty],
  )

  /**
   * The card's two fields, and the only two ways anything in it changes.
   *
   * The new value and the report come out of one call, so the surface can never
   * hear about a draft the card is not holding — and no path edits the card
   * without saying so.
   */
  const setDraftAudio = (next: string | undefined) => {
    setAudioUrl(next)
    announce({audioUrl: next, questions})
  }
  const setDraftQuestions = (next: Slide[]) => {
    setQuestions(next)
    announce({audioUrl, questions: next})
  }

  /** The row whose editor is open, if any. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const clearControl = Dialog.useDialogControl()
  /**
   * What a destructive press would remove, held until the author confirms.
   *
   * Only the *identity* is held, not the copy: the question being deleted is a
   * row that stays on screen until the dialog is answered, and a message built
   * from a stale prompt would name the wrong thing.
   */
  const [pendingDelete, setPendingDelete] = useState<
    {kind: 'questions'} | {kind: 'question'; id: string} | null
  >(null)

  /**
   * The questions that would actually publish.
   *
   * The counter reads *this*, not the row count: a half-written question is a row
   * on screen and not a question in the record, and a counter that counted rows
   * would promise five questions and publish four.
   */
  const usable = useMemo(
    () => questions.filter(question => !!normalizeQuestion(question)),
    [questions],
  )

  const isFull = questions.length >= DOCUMENT_PAGE_MAX_QUESTIONS

  const closeEditor = (id: string) => {
    setEditingId(current => (current === id ? null : current))
  }

  /**
   * Finishes a question, refusing to keep one that could never be answered.
   *
   * The row is removed rather than kept and quietly dropped later: a question with
   * no prompt publishes nothing, and an author who watched a row sit there while
   * the counter ignored it would have been told the opposite. Here they are told
   * once, and the row is gone.
   */
  const finishQuestion = (id: string) => {
    const question = questions.find(row => row.id === id)
    if (question && !normalizeQuestion(question)) {
      setDraftQuestions(questions.filter(row => row.id !== id))
      Toast.show(
        l`A question needs a prompt and a correct answer before it can be kept.`,
        'circle-exclamation',
      )
    }
    closeEditor(id)
  }

  const addQuestion = (type: MaterialQuestionType) => {
    const question = createSlide(type)
    setDraftQuestions([...questions, question])
    setEditingId(question.id)
  }

  const updateQuestion = (id: string, partial: Partial<Slide>) => {
    setDraftQuestions(
      questions.map(row => (row.id === id ? {...row, ...partial} : row)),
    )
  }

  const removeQuestion = (id: string) => {
    setDraftQuestions(questions.filter(row => row.id !== id))
    closeEditor(id)
  }

  /** The question a pending delete would take, for the dialog's own copy. */
  const pendingQuestion =
    pendingDelete?.kind === 'question'
      ? questions.find(row => row.id === pendingDelete.id)
      : undefined
  const pendingTitle =
    pendingQuestion?.question?.trim() ||
    (pendingQuestion
      ? SLIDE_TYPE_META[pendingQuestion.type]?.label || pendingQuestion.type
      : '')

  const confirmDelete = () => {
    const pending = pendingDelete
    setPendingDelete(null)
    if (!pending) return
    if (pending.kind === 'questions') {
      setDraftQuestions([])
      setEditingId(null)
    } else {
      removeQuestion(pending.id)
    }
  }

  const isEmpty = !audioUrl && usable.length === 0

  return (
    <View style={ss.card} testID="pageMaterialEditor">
      <View style={[ss.header, {borderBottomColor: c.border}]}>
        <View style={ss.headerText}>
          <Text style={[ss.title, {color: c.text}]}>
            <Trans>Page {page}</Trans>
          </Text>
          <Text style={[ss.subtitle, {color: c.textMuted}]}>
            <Trans>Audio and questions for this page only</Trans>
          </Text>
        </View>
        <View
          style={[
            ss.counter,
            {
              backgroundColor: isFull ? accent + '22' : c.fill,
              borderColor: isFull ? accent + '66' : c.border,
            },
          ]}>
          <Text
            style={[ss.counterTxt, {color: isFull ? accent : c.textMuted}]}
            testID="pageMaterialCounter">
            {usable.length} / {DOCUMENT_PAGE_MAX_QUESTIONS}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Close without saving`}
          accessibilityHint={l`Discards what you changed on this page`}
          onPress={onCancel}
          style={[ss.close, {backgroundColor: c.fill}]}>
          <Feather name="x" size={15} color={c.text} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={ss.scroll}
        contentContainerStyle={ss.scrollInner}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={ss.section}>
          <Text style={[ss.sectionLabel, {color: c.textMuted}]}>
            <Trans>Audio</Trans>
          </Text>
          <FlashBookAudioField
            url={audioUrl}
            accent={accent}
            onChange={setDraftAudio}
            label={l`PAGE AUDIO`}
          />
        </View>

        <View style={ss.section}>
          <View style={ss.sectionHead}>
            <Text style={[ss.sectionLabel, {color: c.textMuted}]}>
              <Trans>Questions</Trans>
            </Text>
            {questions.length > 0 && (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={l`Remove all questions`}
                accessibilityHint={l`Clears this page's questions`}
                onPress={() => {
                  setPendingDelete({kind: 'questions'})
                  clearControl.open()
                }}>
                <Text style={[ss.clearTxt, {color: c.danger}]}>
                  <Trans>Clear</Trans>
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {questions.map((question, index) => (
            <QuestionRow
              key={question.id}
              index={index}
              slide={question}
              accent={accent}
              isEditing={editingId === question.id}
              onEdit={() => setEditingId(question.id)}
              onDone={() => finishQuestion(question.id)}
              onRemove={() => {
                setPendingDelete({kind: 'question', id: question.id})
                clearControl.open()
              }}
              onChange={partial => updateQuestion(question.id, partial)}
            />
          ))}

          {!isFull && (
            <View style={[ss.addRow]}>
              {MATERIAL_QUESTION_TYPES.map(type => (
                <AddQuestionButton
                  key={type}
                  type={type}
                  accent={accent}
                  onPress={() => addQuestion(type)}
                />
              ))}
            </View>
          )}
          {isFull && (
            <Text style={[ss.hint, {color: c.textMuted}]}>
              <Trans>
                Five questions is the most a page can ask. Remove one to add
                another.
              </Trans>
            </Text>
          )}
        </View>
      </ScrollView>

      <View style={[ss.footer, {borderTopColor: c.border}]}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Cancel`}
          accessibilityHint=""
          onPress={onCancel}
          style={[ss.secondaryBtn, {borderColor: c.borderStrong}]}>
          <Text style={[ss.secondaryTxt, {color: c.text}]}>
            <Trans>Cancel</Trans>
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Confirm this page`}
          accessibilityHint={l`Adds the audio and questions to the page`}
          onPress={() =>
            onConfirm(
              isEmpty
                ? undefined
                : {
                    page,
                    ...(audioUrl ? {audioUrl} : {}),
                    questions: usable,
                  },
            )
          }
          testID="pageMaterialConfirm"
          style={[ss.primaryBtn, {backgroundColor: accent}]}>
          <Feather name="check" size={15} color="#fff" />
          <Text style={ss.primaryTxt}>
            <Trans>Confirm</Trans>
          </Text>
        </TouchableOpacity>
      </View>

      {/**
       * The stop between the trash icon and the loss.
       *
       * It names the question it is about rather than asking "are you sure?" on
       * its own: the author pressed an icon on a row, and the row is what they
       * have to recognise in the answer. The cleared case says what survives —
       * the page's audio is a separate thing, and an author clearing questions
       * has no reason to think a clip is going with them.
       */}
      <StudioConfirmDialog
        control={clearControl}
        title={
          pendingDelete?.kind === 'question'
            ? l`Remove this question?`
            : l`Clear this page's questions?`
        }
        /*
         * Counted from the *rows*, not from the usable questions the counter
         * shows: Clear takes every row on screen, including one still being
         * written, and a message that said "all 0 questions" because the author
         * had not finished typing would be describing a different button.
         */
        message={
          pendingDelete?.kind === 'question'
            ? l`"${pendingTitle}" is removed from page ${page}.`
            : plural(questions.length, {
                one: `The question on page ${page} is removed. Its audio is kept.`,
                other: `All # questions on page ${page} are removed. Its audio is kept.`,
              })
        }
        confirmLabel={
          pendingDelete?.kind === 'question'
            ? l`Remove question`
            : l`Clear questions`
        }
        onConfirm={confirmDelete}
      />
    </View>
  )
}

/** One question, collapsed — and its editor, inline, when it is open. */
function QuestionRow({
  index,
  slide,
  accent,
  isEditing,
  onEdit,
  onDone,
  onRemove,
  onChange,
}: {
  index: number
  slide: Slide
  accent: string
  isEditing: boolean
  onEdit: () => void
  onDone: () => void
  onRemove: () => void
  onChange: (partial: Partial<Slide>) => void
}) {
  const ss = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const meta = SLIDE_TYPE_META[slide.type]
  const glyph = SLIDE_FEATHER_ICON[slide.type] ?? 'help-circle'
  const typeAccent = SLIDE_ACCENT[slide.type] ?? accent
  const prompt = slide.question?.trim()

  return (
    <View style={[ss.question, {borderColor: isEditing ? accent : c.border}]}>
      <View style={ss.questionHead}>
        <View
          style={[
            ss.questionIndex,
            {backgroundColor: accent + '1a', borderColor: accent + '44'},
          ]}>
          <Text style={[ss.questionIndexTxt, {color: accent}]}>
            {index + 1}
          </Text>
        </View>
        <Feather name={glyph} size={13} color={typeAccent} />
        <Text
          style={[ss.questionPrompt, {color: prompt ? c.text : c.textMuted}]}
          numberOfLines={isEditing ? undefined : 1}>
          {prompt || meta?.label || slide.type}
        </Text>
        {!isEditing && (
          <>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Edit this question`}
              accessibilityHint=""
              onPress={onEdit}
              style={ss.iconBtn}>
              <Feather name="edit-2" size={14} color={c.textMuted} />
            </TouchableOpacity>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Remove this question`}
              accessibilityHint=""
              onPress={onRemove}
              style={ss.iconBtn}>
              <Feather name="trash-2" size={14} color={c.danger} />
            </TouchableOpacity>
          </>
        )}
      </View>

      {isEditing && (
        <View style={[ss.editorWrap, {borderTopColor: c.border}]}>
          <QuestionEditor slide={slide} accent={accent} onChange={onChange} />
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Done editing this question`}
            accessibilityHint=""
            onPress={onDone}
            style={[ss.doneBtn, {borderColor: accent}]}>
            <Feather name="check" size={14} color={accent} />
            <Text style={[ss.doneTxt, {color: accent}]}>
              <Trans>Done</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

/**
 * The question's own editor, mounted inline.
 *
 * `inline` is what the FlashBook studio passes for the same reason: these editors
 * otherwise own a fixed-height scroll view, and a fixed height inside a card that
 * is itself scrolling is a region that clips its own footer. `ownsAudio={false}`
 * is the other half — this card has an Audio section of its own above, and an
 * editor that drew the block's reading voice too would give one question two
 * recording controls.
 */
function QuestionEditor({
  slide,
  accent,
  onChange,
}: {
  slide: Slide
  accent: string
  onChange: (partial: Partial<Slide>) => void
}) {
  const props = {
    slide,
    onChange,
    primaryColor: accent,
    inline: true,
    ownsAudio: false,
  }
  switch (slide.type) {
    case 'single_choice':
      return <SingleChoiceSlideEditor {...props} />
    case 'multiple_choice':
      return <MultiChoiceSlideEditor {...props} />
    case 'true_false':
      return <TrueFalseSlideEditor {...props} />
    default:
      return null
  }
}

function AddQuestionButton({
  type,
  accent,
  onPress,
}: {
  type: MaterialQuestionType
  accent: string
  onPress: () => void
}) {
  const ss = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const meta = SLIDE_TYPE_META[type]
  const label = meta?.label ?? type
  const colour = SLIDE_ACCENT[type] ?? accent
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={l`Add a ${label}`}
      accessibilityHint={l`Adds this question to the page`}
      onPress={onPress}
      testID={`pageMaterialAdd-${type}`}
      style={[
        ss.addBtn,
        {borderColor: colour + '55', backgroundColor: colour + '12'},
      ]}>
      <Feather
        name={SLIDE_FEATHER_ICON[type] ?? 'plus'}
        size={13}
        color={colour}
      />
      <Text style={[ss.addTxt, {color: c.text}]}>{label}</Text>
      <Feather name="plus" size={12} color={colour} />
    </TouchableOpacity>
  )
}

/**
 * What a page holds, once it holds something: a bar, not a card.
 *
 * It replaces the `+` control rather than sitting beside it, because the two are
 * the same affordance in two states — "there is nothing here, add some" and "here
 * is what you added". The counts are the sheet's own numbers, so the bar can
 * never claim something the page does not hold, and Preview opens the full screen
 * reader on this page with its questions up — the same play a reader gets, which
 * is the only way an author can check what they will actually receive.
 */
export function PageMaterialBar({
  counts,
  onPreview,
  onEdit,
}: {
  counts: {audio: number; questions: number}
  onPreview: () => void
  onEdit: () => void
}) {
  const ss = useStyles()
  const {t: l} = useLingui()
  return (
    <View style={ss.bar} testID="pageMaterialBar">
      {counts.audio > 0 && (
        <View style={ss.barCount}>
          <Feather name="volume-2" size={12} color="#fff" />
          <Text style={ss.barCountTxt}>{counts.audio}</Text>
        </View>
      )}
      {counts.questions > 0 && (
        <View style={ss.barCount}>
          <Feather name="help-circle" size={12} color="#fff" />
          <Text style={ss.barCountTxt}>{counts.questions}</Text>
        </View>
      )}
      {counts.questions > 0 && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Preview this page's questions`}
          accessibilityHint={l`Opens full screen to play them`}
          onPress={onPreview}
          style={ss.barAction}>
          <Feather name="play" size={12} color="#fff" />
          <Text style={ss.barActionTxt}>
            <Trans>Preview</Trans>
          </Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={l`Edit this page's material`}
        accessibilityHint={l`Opens the audio and questions for this page`}
        onPress={onEdit}
        style={ss.barAction}>
        <Feather name="edit-2" size={12} color="#fff" />
        <Text style={ss.barActionTxt}>
          <Trans>Edit</Trans>
        </Text>
      </TouchableOpacity>
    </View>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    card: {
      position: 'absolute',
      left: 8,
      right: 8,
      bottom: 8,
      maxHeight: '88%',
      borderRadius: 16,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
      overflow: 'hidden',
      shadowColor: c.shadow,
      shadowOpacity: 0.3,
      shadowRadius: 16,
      shadowOffset: {width: 0, height: -4},
      elevation: 12,
      zIndex: 40,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
    },
    headerText: {flex: 1, minWidth: 0},
    title: {fontSize: 15, fontWeight: '800'},
    subtitle: {fontSize: 11, marginTop: 1},
    counter: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 8,
      borderWidth: 1,
    },
    counterTxt: {fontSize: 11, fontWeight: '800'},
    close: {
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    scroll: {flexGrow: 0},
    scrollInner: {padding: 12, gap: 16},
    section: {gap: 8},
    sectionHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    sectionLabel: {
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    clearTxt: {fontSize: 11, fontWeight: '700'},
    question: {borderWidth: 1, borderRadius: 12, overflow: 'hidden'},
    questionHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
    },
    questionIndex: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    questionIndexTxt: {fontSize: 10.5, fontWeight: '800'},
    questionPrompt: {flex: 1, fontSize: 12.5, fontWeight: '600'},
    iconBtn: {padding: 4},
    editorWrap: {borderTopWidth: 1, padding: 6},
    doneBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-end',
      gap: 6,
      marginRight: 8,
      marginBottom: 8,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 10,
      borderWidth: 1,
    },
    doneTxt: {fontSize: 12, fontWeight: '800'},
    addRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6},
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 10,
      borderWidth: 1,
    },
    addTxt: {fontSize: 12, fontWeight: '700'},
    hint: {fontSize: 11, fontStyle: 'italic'},
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderTopWidth: 1,
    },
    secondaryBtn: {
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 12,
      borderWidth: 1,
    },
    secondaryTxt: {fontSize: 13, fontWeight: '700'},
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 12,
    },
    primaryTxt: {fontSize: 13, fontWeight: '800', color: '#fff'},
    // Placed by the carousel, which owns the stage's furniture: the bar is a row,
    // not a position, so it stacks with the alt-text bar instead of fighting it.
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 10,
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
    },
    barCount: {flexDirection: 'row', alignItems: 'center', gap: 3},
    barCountTxt: {color: '#fff', fontSize: 12, fontWeight: '800'},
    barAction: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      marginLeft: 'auto',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
    },
    barActionTxt: {color: '#fff', fontSize: 11.5, fontWeight: '700'},
  })

const useStyles = () => useStudioStyles(makeStyles)
